/**
 * This custom hook provides the functionality to retrieve courses to be shown in the UI.
 */

import React, {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import {useConfirm} from "material-ui-confirm";
import {useIntl} from "react-intl";
import ScoredScenesSectionOfGivenListSlim from "../../videosScored/overview/ScoredScenesSectionOfGivenListSlim";

const useListCourses = () => {

    // State storage of the courses shown in the table
    const [courses, setCourses] = useState([])

    const [loading, setLoading] = useState(true)
    const [deletionInProgress, setDeletionInProgress] = useState(false)
    const [deletionAttemptedCourse, setDeletionAttemptedCourse] = useState(null)

    const {signedInUser} = useContext(FirebaseAuthContext);

    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();

    // Internationalization for simple strings
    const intl = useIntl();

    // Retrieving the courses and setting the list in the state variable
    const fetchCourses = async () => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/courses?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const coursesJson = await data.json();
                setCourses(coursesJson)
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.courses.messages.list.error',
                        defaultMessage: 'Something went wrong while listing the courses: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchCourses();
    }, [])

    const handleDeleteCourse = (course) => {
        setDeletionAttemptedCourse(course.key)
        return checkDeletionAllowed(course)
            .then((checkResult) => {
                setDeletionAttemptedCourse(null)
                if (checkResult.allowed) {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.courses.dialog.delete.confirm.title',
                            defaultMessage: 'Delete Course.'
                        }),
                        description: intl.formatMessage({
                            id: 'app.courses.dialog.delete.confirm.description',
                            defaultMessage: 'Delete course {name}?'
                        }, {name: course.name})
                    })
                        .then(() => 'OK')
                        .catch(() => null)
                } else {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.courses.dialog.delete.generalRejection.title',
                            defaultMessage: 'Deletion not allowed'
                        }),
                        content: <div>
                            <p>{intl.formatMessage({
                                id: 'app.courses.dialog.delete.generalRejection.description',
                                defaultMessage: 'The course is not allowed to be deleted - please contact your administrator.'
                            })}:</p>
                            <ScoredScenesSectionOfGivenListSlim scoredScenes={checkResult.scoredScenes}/>
                        </div>
                    }).then(() => null).catch(() => null)
                }
            })
            .then((clickedButton) => {
                if (clickedButton === 'OK') {
                    return deleteCourse(course)
                }
                return null
            })
            .catch((error) => {
                // Something went wrong (either while the deletion check or while the actual deletion)
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.courses.messages.delete.error',
                        defaultMessage: 'Something went wrong while deleting the course: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setDeletionAttemptedCourse(null)
                setDeletionInProgress(false)
            })
    }

    const checkDeletionAllowed = async (course) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/courses/' + course.key + '/deletionAllowed?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }
                return data.json();
            })
    }

    const deleteCourse = async (course) => {
        setDeletionInProgress(true)
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/courses/' + course.key, {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchCourses()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.courses.messages.delete.success',
                    defaultMessage: 'Course {name} deleted.'
                }, {name: course.name}), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        })
    }

    return {courses, loading, handleDeleteCourse, deletionInProgress, deletionAttemptedCourse}
}
export default useListCourses;